/**
 * Titre de la barre du haut. L'en-tête de page (`PageHeader`) déclare son
 * titre et son retour ; quand il passe sous la barre du haut en défilant, la
 * barre reprend ce titre en petit avec le retour (grand titre qui se replie,
 * comme les applications du téléphone). Tant que l'en-tête est visible, la
 * barre ne le répète pas : jamais deux retours ni deux titres à l'écran.
 */
import {
  createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject,
} from 'react';

export interface PageChrome {
  title: string;
  backTo?: string | undefined;
  backLabel?: string | undefined;
}

interface ChromeState {
  page: PageChrome | undefined;
  /** L'en-tête de la page est-il encore à l'écran, sous la barre du haut ? */
  headerVisible: boolean;
}

interface ChromeControl {
  claim: (token: symbol, page: PageChrome) => void;
  release: (token: symbol) => void;
  setVisible: (token: symbol, visible: boolean) => void;
}

const ChromeStateContext = createContext<ChromeState>({ page: undefined, headerVisible: true });
const ChromeControlContext = createContext<ChromeControl | undefined>(undefined);

export function ShellChromeProvider({ children }: { children: ReactNode }): ReactNode {
  const [state, setState] = useState<ChromeState & { owner?: symbol }>({ page: undefined, headerVisible: true });
  const control = useMemo<ChromeControl>(() => ({
    // Le premier en-tête monté est celui de la page ; un second (aperçu, bloc) n'y touche pas.
    claim: (token, page) => setState((current) =>
      current.owner && current.owner !== token ? current : { owner: token, page, headerVisible: current.owner === token ? current.headerVisible : true }),
    release: (token) => setState((current) =>
      current.owner === token ? { page: undefined, headerVisible: true } : current),
    setVisible: (token, visible) => setState((current) =>
      current.owner === token && current.headerVisible !== visible ? { ...current, headerVisible: visible } : current),
  }), []);
  return (
    <ChromeControlContext.Provider value={control}>
      <ChromeStateContext.Provider value={state}>{children}</ChromeStateContext.Provider>
    </ChromeControlContext.Provider>
  );
}

export function useShellChrome(): ChromeState {
  return useContext(ChromeStateContext);
}

/**
 * Déclare l'en-tête de la page à la coque et suit sa visibilité. Sans coque
 * (galerie, pages publiques) ou sans IntersectionObserver, ne fait rien.
 */
export function usePageChrome(page: PageChrome, header: RefObject<HTMLElement | null>): void {
  const control = useContext(ChromeControlContext);
  const token = useRef(Symbol('en-tete')).current;
  const { title, backTo, backLabel } = page;
  useEffect(() => {
    if (!control) return undefined;
    control.claim(token, { title, backTo, backLabel });
    return undefined;
  }, [control, token, title, backTo, backLabel]);
  useEffect(() => {
    if (!control) return undefined;
    return () => control.release(token);
  }, [control, token]);
  useEffect(() => {
    const node = header.current;
    if (!control || !node || typeof IntersectionObserver === 'undefined') return undefined;
    // La barre du haut recouvre le haut de l'écran : l'en-tête est « parti »
    // dès qu'il passe dessous, pas seulement quand il quitte l'écran.
    const topbar = document.querySelector<HTMLElement>('.app-topbar')?.getBoundingClientRect().height ?? 0;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry) control.setVisible(token, entry.isIntersecting); },
      { rootMargin: `-${Math.round(topbar)}px 0px 0px 0px` },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [control, token, header]);
}
